import { DeleteOutlined, Info } from '@mui/icons-material';
import React, { useCallback, useRef, useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import LoadingDots from '../Animations/LoadingDots';
import { Instructions } from '../Validate/Validation/Instructions';
import { API } from '../../config/api';
import axios from 'axios';
import ValidationComplete from '../Validate/ValidationComplete';
import ErrorModal from '../Validate/ErrorModal';

interface FileItem {
  name: string;
  size: number;
  id: string;
  loading?: boolean;
  file: File;
}

interface FileError {
  message: string;
  code: string;
  details?: any;
}

interface TimestampResponse {
  success: boolean;
  statusCode: number;
  message: string;
  requestId: string;
  timestamp: string;
  data: {
    uid: string;
    hash: string;
  };
}

const UploadBox = () => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [fileErrors, setFileErrors] = useState<FileError[]>([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [rejectedFilesArray, setRejectedFilesArray] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // const [proofOption, setProofOption] = useState<'timestamp' | 'onchain'>('timestamp');

  const handleFileChange = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles) return;
    // TODO 1 add restricted files functions
    const newFiles = Array.from(selectedFiles).map((file) => ({
      name: file.name,
      size: file.size,
      id: `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file: file,
    }));

    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      handleFileChange(e.dataTransfer.files);
    },
    [handleFileChange],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDragging) {
        setIsDragging(true);
      }
    },
    [isDragging],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragging to false if we're leaving the actual drop zone
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
      setIsDragging(false);
    }
  }, []);

  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => prev.filter((file) => file.id !== fileId));
  }, []);

  const handleUpload = useCallback(
    async (e: React.MouseEvent) => {
      // Prevent default to avoid any form submission
      e.preventDefault();

      // Clear any previous errors
      setFileErrors([]);
      setShowErrorModal(false);

      // Mark all files as loading
      setFiles((prev) => prev.map((file) => ({ ...file, loading: true })));

      // Set component to loading state
      setIsLoading(true);

      try {
        // Process each file
        for (const fileItem of files) {
          // Create FormData for file upload
          const formData = new FormData();
          formData.append('file', fileItem.file);

          // Step 1: Post file to get timestamp
          const timestampResponse = await axios.post<TimestampResponse>(
            API.TIMESTAMP.post,
            formData,
            {
              withCredentials: true,
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            },
          );
          // console.log('timestampResponse', timestampResponse);

          const { requestId, data: timestampData } = timestampResponse.data;

          // Step 2: Insert timestamp into database
          await axios.post(
            API.TIMESTAMP.insert,
            {
              FILE_NAME: fileItem.name,
              REQ_ID: requestId,
              UID: timestampData.uid,
              HASH: timestampData.hash,
            },
            { withCredentials: true },
          );
        }

        // If all files processed successfully, show completion
        setIsLoading(false);
        setIsCompleted(true);
      } catch (error: any) {
        console.error('Error uploading files:', error);
        // Handle error state with detailed error information
        setFileErrors([
          {
            message:
              error?.response?.data?.message ||
              error?.response?.data?.error ||
              error?.message ||
              'Unknown error occurred while uploading files. Please contact support.',
            code: 'API_ERROR',
            details: error?.response?.data?.errors?.[0] || error?.response?.data?.details || null,
          },
        ]);
        setFiles([]);
        setShowErrorModal(true);
        setIsLoading(false);
      }
    },
    [files],
  );

  // Handle upload more files button click
  const handleUploadMore = useCallback(() => {
    setIsCompleted(false);
    setFiles([]);
  }, []);

  // Truncate file name if it's too long
  const truncateFileName = (fileName: string) => {
    if (fileName.length <= 20) return fileName;
    const extension = fileName.split('.').pop();
    const name = fileName.substring(0, 10);
    return `${name}...${extension}`;
  };

  // If completed, show the new completion view
  if (isCompleted) {
    return (
      <ValidationComplete handleUploadMoreFiles={handleUploadMore} type="timestamp" />
      // <section className="flex flex-col justify-between items-start w-[424px] h-[540px] p-10 rounded-3xl bg-black text-white">
      //   <div className="self-stretch grow shrink basis-0 flex-col justify-center items-center flex">
      //     <div className="flex flex-col h-full w-full items-center justify-center gap-4 opacity-0 translate-y-10 animate-slide-up-main">
      //       <div className="w-20 h-20 relative">
      //         <div className="w-20 h-20 left-0 top-0 absolute"></div>
      //         <div data-svg-wrapper="true" className="left-[8.33px] top-[8.33px] absolute">
      //           <svg
      //             width="64"
      //             height="64"
      //             viewBox="0 0 64 64"
      //             fill="none"
      //             xmlns="http://www.w3.org/2000/svg"
      //           >
      //             <path
      //               d="M27.0833 46.045L49.545 23.5834L46.6408 20.7309L27.0833 40.3209L17.2116 30.4492L14.3908 33.3017L27.0833 46.045ZM32.0058 63.6667C27.6603 63.6667 23.5658 62.8356 19.7225 61.1734C15.8791 59.5112 12.5183 57.2414 9.63998 54.3642C6.76165 51.487 4.49081 48.1275 2.82748 44.2859C1.1647 40.4448 0.333313 36.3514 0.333313 32.0059C0.333313 27.6259 1.16442 23.5089 2.82665 19.655C4.48887 15.8012 6.75859 12.4489 9.63581 9.59837C12.513 6.74782 15.8725 4.49087 19.7141 2.82754C23.5553 1.16476 27.6486 0.333374 31.9941 0.333374C36.3741 0.333374 40.4911 1.16448 44.345 2.82671C48.1989 4.48893 51.5511 6.74476 54.4016 9.59421C57.2522 12.4437 59.5091 15.7945 61.1725 19.6467C62.8353 23.4989 63.6666 27.6148 63.6666 31.9942C63.6666 36.3398 62.8355 40.4342 61.1733 44.2775C59.5111 48.1209 57.2553 51.4817 54.4058 54.36C51.5564 57.2384 48.2055 59.5092 44.3533 61.1725C40.5011 62.8353 36.3853 63.6667 32.0058 63.6667Z"
      //               fill="#4FE3C1"
      //             />
      //           </svg>
      //         </div>
      //       </div>
      //       <p className="self-stretch text-center text-[22px] font-medium leading-[30.80px]">
      //         Your files have been uploaded
      //       </p>
      //     </div>
      //     <div className="self-stretch h-28 flex-col justify-start items-start gap-4 flex">
      //       <a
      //         className="w-full py-2 rounded-md text-black bg-minima-orange-2023 cursor-pointer hover:bg-minima-orange-light transition-colors duration-600 ease-in-out text-center"
      //         href="#/uploaded"
      //       >
      //         Proof results
      //       </a>
      //       <button
      //         className="w-full py-2 rounded-md text-white bg-core-black-contrast cursor-pointer hover:bg-opacity-80 transition-colors duration-600 ease-in-out"
      //         onClick={handleUploadMore}
      //       >
      //         Upload more files
      //       </button>
      //     </div>
      //   </div>
      // </section>
    );
  }

  return (
    <section
      className="flex flex-col items-center justify-between w-full max-w-[424px] min-w-[280px] h-[540px] p-4 sm:p-6 md:p-10 bg-black text-white rounded-3xl 
                transition-all duration-300 animate-slide-up-main relative"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {showErrorModal ? (
        <ErrorModal
          fileErrors={fileErrors}
          setFileErrors={setFileErrors}
          rejectedFilesArray={rejectedFilesArray}
          setRejectedFilesArray={setRejectedFilesArray}
          setShowErrorModal={setShowErrorModal}
        />
      ) : (
        <>
          {/* Improved drag overlay that fully covers content */}
          {isDragging && (
            <div className="absolute inset-0 bg-white flex items-center justify-center rounded-3xl z-10">
              <p className="text-black text-xl font-medium text-center px-6">
                Upload files by dropping them in this window
              </p>
            </div>
          )}

          {isLoading ? (
            /* Loading state UI */
            <div className="flex flex-col h-full w-full items-center gap-4">
              <div className="w-full flex items-center gap-4 animate-fade-in-first">
                <input
                  type="file"
                  hidden
                  id="browse"
                  ref={fileInputRef}
                  multiple
                  onChange={(e) => handleFileChange(e.target.files)}
                />
                <label
                  htmlFor="browse"
                  className="w-10 h-10 text-black flex items-center justify-center rounded-full font-bold text-[30px] 
                bg-minima-orange-2023 cursor-pointer hover:bg-minima-orange-light hover:border-solid transition-all duration-600 ease-linear"
                >
                  +
                </label>
                <div className="flex flex-col items-start">
                  <p className="text-lg font-normal">Drag and drop files</p>
                  <p className="text-sm font-normal text-gray-400">Or select files</p>
                </div>
              </div>
              <div className="h-[200px] w-full overflow-y-auto flex flex-col items-center scroll-container gap-2 animate-fade-in-second">
                {files.map((file) => (
                  <div className="w-full" key={file.id}>
                    <div className="h-[46px] w-full flex items-center bg-core-black-contrast p-3 rounded-sm text-sm text-core-grey-60">
                      <p className="text-sm">{truncateFileName(file.name)}</p>
                      <div className="ml-auto flex items-center mr-2">
                        {file.loading && <LoadingDots spinningRadius={10} heightWidthPx="24px" />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Entry state UI
            <>
              <div className="flex flex-col h-full w-full items-center gap-4">
                {files.length === 0 ? (
                  <div className="w-full flex flex-col justify-center items-center h-full">
                    <input
                      type="file"
                      hidden
                      id="browse"
                      ref={fileInputRef}
                      multiple
                      onChange={(e) => handleFileChange(e.target.files)}
                    />
                    <label
                      htmlFor="browse"
                      className="w-20 h-20 bg-minima-orange-2023 text-black flex items-center justify-center rounded-full font-medium text-[80px] cursor-pointer hover:bg-minima-orange-light hover:transition-all duration-600 ease-linear"
                    >
                      +
                    </label>
                    <div className="flex flex-col items-center text-center">
                      <p className="mt-3 text-[18px] font-medium">Drag and drop files</p>
                      <p className="text-[14px] text-core-grey-80 font-medium">Or select files</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* File added UI */}
                    <div className="w-full flex items-center gap-4 animate-fade-in-first">
                      <input
                        type="file"
                        hidden
                        id="browse"
                        ref={fileInputRef}
                        multiple
                        onChange={(e) => handleFileChange(e.target.files)}
                      />
                      <label
                        htmlFor="browse"
                        className="w-10 h-10 text-black flex items-center justify-center rounded-full font-bold text-[30px] bg-minima-orange-2023 cursor-pointer hover:bg-minima-orange-light hover:border-solid transition-all duration-600 ease-linear"
                      >
                        +
                      </label>
                      <div className="flex flex-col items-start">
                        <p className="text-lg font-normal">Drag and drop files</p>
                        <p className="text-sm font-normal text-gray-400">Or select files</p>
                      </div>
                    </div>
                    <div className="h-[200px] w-full overflow-y-auto flex flex-col items-center scroll-container gap-2 animate-fade-in-second">
                      {files.map((file) => (
                        <div className="w-full" key={file.id}>
                          <div className="h-[46px] w-full flex items-center bg-core-black-contrast p-3 rounded-sm text-sm text-core-grey-60">
                            <p className="text-sm">{truncateFileName(file.name)}</p>
                            <div className="ml-auto flex items-center">
                              {file.loading && (
                                <LoadingDots spinningRadius={10} heightWidthPx="24px" />
                              )}
                              <DeleteIcon
                                onClick={() => removeFile(file.id)}
                                className="ml-auto hover:text-minima-core-orange cursor-pointer"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              {/* <div className="flex gap-2 mb-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="proofOption"
                value="timestamp"
                checked={proofOption === 'timestamp'}
                onChange={() => setProofOption('timestamp')}
                className="accent-minima-orange-2023"
              />
              Stamp
            </label>

            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="proofOption"
                value="onchain"
                checked={proofOption === 'onchain'}
                onChange={() => setProofOption('onchain')}
                className="accent-minima-orange-2023"
              />
              On-chain
            </label>
          </div> */}
            </>
          )}

          <div className="flex flex-col w-full gap-2">
            <div className="flex flex-col gap-[8px] text-xs">
              {/* {files.length > 0 && (
            <p className="text-core-grey-80 font-semibold">
              Fee: <span className="text-white">0.0 Minima</span>
            </p>
          )} */}

              {/* <Instructions /> */}
            </div>

            <div className="flex flex-col items-center gap-[5px]">
              <button
                onClick={handleUpload}
                disabled={files.length === 0}
                className={`btn-primary text-black transition-colors duration-600 ease-in-out ${
                  files.length === 0
                    ? 'bg-brown-orange cursor-not-allowed'
                    : 'bg-minima-orange-2023 cursor-pointer hover:bg-minima-orange-light'
                }`}
              >
                Stamp
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default UploadBox;
